import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import CustomInput from "../../../../Components/UI/Inputs/CustomInput";
import BoltButton from "../../../../Components/UI/Buttons/BoltButton";
import { showSnackBar } from "../../../../store/common/snackBarSlice";
import { FaPlus, FaTrash } from "react-icons/fa";
import { MdOutlineErrorOutline } from "react-icons/md";
import Confirm from "../../../../Components/Popups/Confirm";

import {
  createUserPaymentFormula,
  deleteUserPaymentFormula,
  updateUserPaymentFormula,
  getUserPaymentFormula,
  getMarkupFormula,
} from "../../../../services/AdminServices/PaymentsAdmin/payments.services";
import { useTranslation } from "react-i18next";
import i18n from "../../../../i18n";

const Formula = ({ shipperData, updateShipper }) => {
  const { t } = useTranslation("admin");
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("tier");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  const [paymentFormula, setPaymentFormula] = useState(null);
  const [userDefaultFormula, setUserDefaultFormula] = useState(null);
  const [markupFormula, setMarkupFormula] = useState(null);
  const [userDefaultMarkup, setUserDefaultMarkup] = useState(null);

  const [newDetail, setNewDetail] = useState({ min: "", max: "" });
  const [error, setError] = useState("");
  const [selectedState, setSelectedState] = useState("");

  const taxConfig = {
    gst: {
      states: [
        "Alberta",
        "British Columbia",
        "Manitoba",
        "Northwest Territories",
        "Nunavut",
        "Quebec",
        "Saskatchewan",
        "Yukon",
      ],
      value: 5,
      name: "GST",
    },
    hst1: {
      states: ["Ontario"],
      value: 13,
      name: "HST",
    },
    hst2: {
      states: [
        "New Brunswick",
        "Newfoundland and Labrador",
        "Nova Scotia",
        "Prince Edward Island",
      ],
      value: 15,
      name: "HST",
    },
    qst: {
      states: ["Quebec"],
      value: 9.975,
      name: "QST",
    },
  };

  const loadFormulaData = useCallback(async () => {
    setInitialLoading(true);
    try {
      const formulaResponse = await getUserPaymentFormula();
      setUserDefaultFormula(formulaResponse?.data?.result);

      if (shipperData?.formula) {
        setPaymentFormula({
          ...shipperData.formula,
          deletedDetailsIds: [],
          deletedTaxesIds: [],
        });
      } else {
        setPaymentFormula(formulaResponse?.data?.result);
      }

      const markupResponse = await getMarkupFormula();
      setUserDefaultMarkup(markupResponse?.data?.result);

      if (shipperData?.markup_formula) {
        setMarkupFormula(shipperData.markup_formula);
      } else {
        setMarkupFormula(markupResponse?.data?.result);
      }

      setNewDetail({ min: "", max: "" });
    } catch (error) {
      dispatch(
        showSnackBar({
          text: i18n.t("admin:controls.failedToLoadFormulaData"),
          severity: "error",
        })
      );
    } finally {
      setInitialLoading(false);
    }
  }, [shipperData, dispatch]);

  useEffect(() => {
    if (shipperData) {
      loadFormulaData();
    }
  }, [shipperData, loadFormulaData]);

  useEffect(() => {
    if (paymentFormula?.formula_details?.length > 0) {
      const lastDetail =
        paymentFormula.formula_details[
          paymentFormula.formula_details.length - 1
        ];
      const nextMin = Number(lastDetail.max) + 1;
      const nextMax =
        nextMin + (Number(lastDetail.max) - Number(lastDetail.min));

      setNewDetail({
        min: nextMin ? nextMin.toString() : "1",
        max: nextMax ? nextMax.toString() : "50",
      });
    } else {
      setNewDetail({ min: "1", max: "50" });
    }
  }, [paymentFormula?.formula_details, isEditMode]);

  if (!shipperData) return null;

  const updateFormulaField = (field, value) => {
    setPaymentFormula((prev) => ({ ...prev, [field]: value }));
  };

  const updateFormulaDetail = (index, field, value) => {
    const newDetails = [...paymentFormula.formula_details];
    newDetails[index][field] = value;
    setPaymentFormula((prev) => ({ ...prev, formula_details: newDetails }));
  };

  const updateFormulaTax = (index, field, value) => {
    const newTaxes = [...paymentFormula.formula_taxes];
    newTaxes[index][field] = value;
    setPaymentFormula((prev) => ({ ...prev, formula_taxes: newTaxes }));
  };

  const addFormulaDetail = () => {
    if (!newDetail.min || !newDetail.max) {
      setError(t("controls.pleaseFillAllFields"));
      return;
    }

    if (Number(newDetail.min) >= Number(newDetail.max)) {
      setError(t("controls.minMustBeLessThan"));
      return;
    }

    if (paymentFormula.formula_details.length > 0) {
      const lastMax =
        paymentFormula.formula_details[
          paymentFormula.formula_details.length - 1
        ].max;
      if (Number(newDetail.min) <= Number(lastMax)) {
        setError(t("controls.minMustBeGreaterThan"));
        return;
      }
    }

    setError("");
    const newDetailWithValue = { ...newDetail, value: "" };

    setPaymentFormula((prev) => ({
      ...prev,
      formula_details: [...prev.formula_details, newDetailWithValue],
    }));

    setNewDetail({ min: "", max: "" });
  };

  const removeFormulaDetail = (index) => {
    const detailToRemove = paymentFormula.formula_details[index];
    const newDetails = paymentFormula.formula_details.filter(
      (_, i) => i !== index
    );

    const updatedDeletedDetailsIds = [
      ...(paymentFormula.deletedDetailsIds || []),
    ];

    if (detailToRemove?.id) {
      if (!updatedDeletedDetailsIds.includes(detailToRemove.id)) {
        updatedDeletedDetailsIds.push(detailToRemove.id);
      }
    }

    setPaymentFormula((prev) => ({
      ...prev,
      formula_details: newDetails,
      deletedDetailsIds: updatedDeletedDetailsIds,
    }));
  };

  const addFormulaTax = (stateDisplay = null) => {
    if (stateDisplay) {
      if (stateDisplay === "Quebec (GST + QST)") {
        const applicableTaxes = [];
        const updatedTaxes = [...paymentFormula.formula_taxes];

        const quebecTaxes = [
          { key: "gst", config: taxConfig.gst },
          { key: "qst", config: taxConfig.qst },
        ];

        quebecTaxes.forEach(({ key, config }) => {
          const existingTaxIndex = updatedTaxes.findIndex(
            (existingTax) => existingTax.name === config.name
          );

          if (existingTaxIndex !== -1) {
            const existingTax = updatedTaxes[existingTaxIndex];
            if (existingTax.value !== config.value.toString()) {
              updatedTaxes[existingTaxIndex] = {
                ...existingTax,
                value: config.value.toString(),
                state: "Quebec",
                taxType: key,
              };
              dispatch(
                showSnackBar({
                  text: t("controls.taxValueUpdated", { name: config.name, value: config.value }),
                  severity: "info",
                })
              );
            }
          } else {
            applicableTaxes.push({
              name: config.name,
              value: config.value.toString(),
              state: "Quebec",
              taxType: key,
            });
          }
        });

        setPaymentFormula((prev) => ({
          ...prev,
          formula_taxes: [...updatedTaxes, ...applicableTaxes],
        }));

        if (applicableTaxes.length > 0) {
          dispatch(
            showSnackBar({
              text: t("controls.taxesAddedQuebec", { count: applicableTaxes.length }),
              severity: "success",
            })
          );
        }
      } else {
        const stateName = stateDisplay.split(" (")[0];
        const applicableTaxes = [];
        const updatedTaxes = [...paymentFormula.formula_taxes];

        Object.entries(taxConfig).forEach(([key, config]) => {
          if (config.states.includes(stateName)) {
            const existingTaxIndex = updatedTaxes.findIndex(
              (existingTax) => existingTax.name === config.name
            );

            if (existingTaxIndex !== -1) {
              const existingTax = updatedTaxes[existingTaxIndex];
              if (existingTax.value !== config.value.toString()) {
                updatedTaxes[existingTaxIndex] = {
                  ...existingTax,
                  value: config.value.toString(),
                  state: stateName,
                  taxType: key,
                };
                dispatch(
                  showSnackBar({
                    text: t("controls.taxValueUpdated", { name: config.name, value: config.value }),
                    severity: "info",
                  })
                );
              }
            } else {
              applicableTaxes.push({
                name: config.name,
                value: config.value.toString(),
                state: stateName,
                taxType: key,
              });
            }
          }
        });

        setPaymentFormula((prev) => ({
          ...prev,
          formula_taxes: [...updatedTaxes, ...applicableTaxes],
        }));

        if (applicableTaxes.length > 0) {
          dispatch(
            showSnackBar({
              text: t("controls.taxesAdded", { count: applicableTaxes.length }),
              severity: "success",
            })
          );
        }
      }
    } else {
      setPaymentFormula((prev) => ({
        ...prev,
        formula_taxes: [
          ...prev.formula_taxes,
          { name: "", value: "", state: "", taxType: "custom" },
        ],
      }));
    }
  };

  const removeFormulaTax = (index) => {
    const taxToRemove = paymentFormula.formula_taxes[index];
    const newTaxes = paymentFormula.formula_taxes.filter((_, i) => i !== index);

    const updatedDeletedTaxesIds = [...(paymentFormula.deletedTaxesIds || [])];

    if (taxToRemove?.id) {
      if (!updatedDeletedTaxesIds.includes(taxToRemove.id)) {
        updatedDeletedTaxesIds.push(taxToRemove.id);
      }
    }

    setPaymentFormula((prev) => ({
      ...prev,
      formula_taxes: newTaxes,
      deletedTaxesIds: updatedDeletedTaxesIds,
    }));
  };

  const updateMarkupValue = (value) => {
    setMarkupFormula((prev) => ({
      ...prev,
      formula_details: [{ ...prev.formula_details[0], value }],
    }));
  };

  const saveFormula = async () => {
    if (paymentFormula.formula_taxes.length === 0) {
      dispatch(
        showSnackBar({
          text: t("controls.pleaseAddAtLeastOne"),
          severity: "error",
        })
      );
      return;
    }

    if (paymentFormula.formula_details.length > 0) {
      const emptyFeeIndices = paymentFormula.formula_details
        .map((detail, index) =>
          !detail.value || detail.value.trim() === "" ? index : null
        )
        .filter((index) => index !== null);

      if (emptyFeeIndices.length > 0) {
        dispatch(
          showSnackBar({
            text: t("controls.pleaseFillInAllFee"),
            severity: "error",
          })
        );
        return;
      }
    }

    setLoading(true);
    try {
      const transformedTaxes = paymentFormula.formula_taxes.map((tax) => {
        if (tax.taxType === "hst1" || tax.taxType === "hst2") {
          return { ...tax, name: "HST" };
        }
        return tax;
      });

      const payload = {
        type: paymentFormula.type,
        formulaDetails: paymentFormula.formula_details,
        formulaTaxes: transformedTaxes,
        deletedDetailsIds: paymentFormula.deletedDetailsIds || [],
        deletedTaxesIds: paymentFormula.deletedTaxesIds || [],
      };

      let response;
      if (paymentFormula.id) {
        response = await updateUserPaymentFormula(payload, paymentFormula.id);
      } else {
        response = await createUserPaymentFormula(payload, shipperData.id);
      }

      if (response.error) {
        dispatch(
          showSnackBar({
            text:
              response?.error?.response?.data?.message ||
              t("controls.errorSavingFormula"),
            severity: "error",
          })
        );
      } else {
        dispatch(
          showSnackBar({
            text: response?.data?.message || t("controls.formulaSavedSuccessfully"),
            severity: "success",
          })
        );

        setPaymentFormula({
          ...response?.data?.result,
          deletedDetailsIds: [],
          deletedTaxesIds: [],
        });

        // ✅ Update parent user state
        if (updateShipper) {
          updateShipper(shipperData.id, "formula", response?.data?.result);
        }

        setIsEditMode(false);
        setIsCreatingNew(false);
      }
    } catch (error) {
      dispatch(
        showSnackBar({
          text: t("controls.somethingWentWrong"),
          severity: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const saveMarkup = async () => {
    setLoading(true);
    try {
      const payload = {
        type: "by_markup",
        formulaDetails: markupFormula.formula_details,
      };

      let response;
      if (markupFormula.id) {
        response = await updateUserPaymentFormula(payload, markupFormula.id);
      } else {
        response = await createUserPaymentFormula(payload, shipperData.id);
      }

      if (response.error) {
        dispatch(
          showSnackBar({
            text:
              response?.error?.response?.data?.message || t("controls.errorSavingMarkup"),
            severity: "error",
          })
        );
      } else {
        dispatch(
          showSnackBar({
            text: response?.data?.message || t("controls.markupSavedSuccessfully"),
            severity: "success",
          })
        );

        setMarkupFormula(response?.data?.result);

        // ✅ Update parent user state
        if (updateShipper) {
          updateShipper(
            shipperData.id,
            "markup_formula",
            response?.data?.result
          );
        }

        setIsEditMode(false);
        setIsCreatingNew(false);
      }
    } catch (error) {
      dispatch(
        showSnackBar({
          text: t("controls.somethingWentWrong"),
          severity: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const resetToDefault = async () => {
    setLoading(true);
    try {
      const formulaToReset = confirmDelete;
      const response = await deleteUserPaymentFormula(formulaToReset.id);

      if (response.error) {
        dispatch(
          showSnackBar({
            text:
              response?.error?.response?.data?.message ||
              t("controls.errorResettingToDefault"),
            severity: "error",
          })
        );
      } else {
        dispatch(
          showSnackBar({
            text: response?.data?.message || t("controls.resetToDefaultSuccessfully"),
            severity: "success",
          })
        );

        if (activeTab === "tier") {
          setPaymentFormula(userDefaultFormula);
          // ✅ Update parent user state
          if (updateShipper) {
            updateShipper(shipperData.id, "formula", null);
          }
        } else {
          setMarkupFormula(userDefaultMarkup);
          // ✅ Update parent user state
          if (updateShipper) {
            updateShipper(shipperData.id, "markup_formula", null);
          }
        }
      }
      setConfirmDelete(false);
    } catch (error) {
      dispatch(
        showSnackBar({
          text: t("controls.somethingWentWrong"),
          severity: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const SkeletonLoader = () => (
    <div className="space-y-4 animate-pulse">
      {/* Header Skeleton */}
      <div className="bg-ui-surface rounded-lg border border-ui-border p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div>
              <div className="h-6 bg-ui-border rounded w-40 mb-2"></div>
              <div className="h-4 bg-ui-border rounded w-24"></div>
            </div>
            <div className="flex bg-ui-surface-sunken rounded-lg p-1">
              <div className="h-8 bg-ui-border rounded-md w-24 mr-2"></div>
              <div className="h-8 bg-ui-border rounded-md w-20"></div>
            </div>
          </div>
          <div className="flex space-x-2">
            <div className="h-8 bg-ui-border rounded w-20"></div>
            <div className="h-8 bg-ui-border rounded w-16"></div>
          </div>
        </div>
      </div>

      {/* Content Skeleton */}
      <div className="bg-ui-surface rounded-lg border border-ui-border p-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Table Skeleton */}
          <div className="space-y-3">
            <div className="h-5 bg-ui-border rounded w-24"></div>
            <div className="bg-ui-surface border border-ui-border rounded-lg overflow-hidden">
              <div className="bg-ui-surface-sunken p-3">
                <div className="grid grid-cols-3 gap-4">
                  <div className="h-4 bg-ui-border rounded"></div>
                  <div className="h-4 bg-ui-border rounded"></div>
                  <div className="h-4 bg-ui-border rounded"></div>
                </div>
              </div>
              <div className="p-3 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="grid grid-cols-3 gap-4">
                    <div className="h-6 bg-ui-border rounded"></div>
                    <div className="h-6 bg-ui-border rounded"></div>
                    <div className="h-6 bg-ui-border rounded w-16"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Table Skeleton */}
          <div className="space-y-3">
            <div className="h-5 bg-ui-border rounded w-20"></div>
            <div className="bg-ui-surface border border-ui-border rounded-lg overflow-hidden">
              <div className="bg-ui-surface-sunken p-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="h-4 bg-ui-border rounded"></div>
                  <div className="h-4 bg-ui-border rounded"></div>
                </div>
              </div>
              <div className="p-3 space-y-3">
                {[1, 2].map((i) => (
                  <div key={i} className="grid grid-cols-2 gap-4">
                    <div className="h-6 bg-ui-border rounded"></div>
                    <div className="h-6 bg-ui-border rounded w-16"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (initialLoading) {
    return <SkeletonLoader />;
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Compact Header */}
      <div className="bg-ui-surface rounded-lg border border-ui-border p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div className="flex flex-col sm:flex-row sm:items-start space-y-2 sm:space-y-0 sm:space-x-4">
            <div>
              <div className="text-base sm:text-lg font-semibold text-ui-text">
                {t("controls.paymentFormula")}
              </div>
              {activeTab === "tier" ? (
                <p className="text-xs text-ui-text-muted">
                  {paymentFormula?.is_default === "true" ||
                  paymentFormula?.is_default === null
                    ? t("controls.defaultFormula")
                    : t("controls.customFormula")}
                </p>
              ) : (
                <p className="text-xs text-ui-text-muted mt-1">
                  {markupFormula?.is_default === "for_markup_default" ||
                  markupFormula?.is_default === null
                    ? t("controls.defaultMarkup")
                    : t("controls.customMarkup")}
                </p>
              )}
            </div>
            <div className="flex bg-ui-surface-sunken rounded-lg p-1">
              <button
                onClick={() => setActiveTab("tier")}
                className={`px-2 sm:px-3 py-1 rounded-md text-xs font-medium transition-all ${
                  activeTab === "tier"
                    ? "bg-ui-surface text-brand shadow-sm"
                    : "text-ui-text-muted hover:text-ui-text"
                }`}
              >
                <span className="hidden sm:inline">{t("controls.tierPricing")}</span>
                <span className="sm:hidden">{t("controls.tier")}</span>
              </button>
              <button
                onClick={() => setActiveTab("markup")}
                className={`px-2 sm:px-3 py-1 rounded-md text-xs font-medium transition-all ${
                  activeTab === "markup"
                    ? "bg-ui-surface text-brand shadow-sm"
                    : "text-ui-text-muted hover:text-ui-text"
                }`}
              >
                <span className="hidden sm:inline">{t("controls.markupPercent")}</span>
                <span className="sm:hidden">{t("controls.markup")}</span>
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-1 sm:space-x-2 flex-wrap">
            {activeTab === "tier" ? (
              <>
                {!isEditMode &&
                  (paymentFormula?.is_default === "true" ||
                    paymentFormula?.is_default === null) && (
                    <BoltButton
                      label={t("controls.createFormula")}
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        setIsCreatingNew(true);
                        setIsEditMode(true);

                        setPaymentFormula({
                          type: "by_shipment",
                          formula_details: [],
                          formula_taxes: [],
                          deletedDetailsIds: [],
                          deletedTaxesIds: [],
                        });
                      }}
                      className="h-8 px-3 text-xs"
                    />
                  )}
                {!isEditMode && paymentFormula?.is_default === "false" && (
                  <>
                    <BoltButton
                      label={t("common:actions.edit")}
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        setIsCreatingNew(false);
                        setIsEditMode(true);
                      }}
                      className="h-8 px-3 text-xs"
                    />
                    <BoltButton
                      label={t("controls.resetToDefault")}
                      variant="danger"
                      size="sm"
                      onClick={() => setConfirmDelete(paymentFormula)}
                      className="h-8 px-3 text-xs"
                    />
                  </>
                )}
              </>
            ) : (
              <>
                {!isEditMode &&
                  (markupFormula?.is_default === "for_markup_default" ||
                    markupFormula?.is_default === null) && (
                    <BoltButton
                      label={t("controls.createMarkup")}
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        setIsCreatingNew(true);
                        setIsEditMode(true);

                        setMarkupFormula({
                          type: "by_markup",
                          formula_details: [{ value: "" }],
                          deletedDetailsIds: [],
                          deletedTaxesIds: [],
                        });
                      }}
                      className="h-8 px-3 text-xs"
                    />
                  )}
                {!isEditMode &&
                  markupFormula?.is_default !== "for_markup_default" &&
                  markupFormula?.id && (
                    <>
                      <BoltButton
                        label={t("common:actions.edit")}
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          setIsCreatingNew(false);
                          setIsEditMode(true);
                        }}
                        className="h-8 px-3 text-xs"
                      />
                      <BoltButton
                        label={t("common:actions.reset")}
                        variant="danger"
                        size="sm"
                        onClick={() => setConfirmDelete(markupFormula)}
                        className="h-8 px-3 text-xs"
                      />
                    </>
                  )}
              </>
            )}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === "tier" ? (
          <div className="space-y-3 sm:space-y-4">
            {/* Compact Layout: Method + Add Tier */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
              {/* Calculation Method */}
              <div className="space-y-2">
                <label className="block text-xs font-medium text-ui-text">
                  {t("controls.calculationMethod")}
                </label>
                <div className="flex space-x-2">
                  <button
                    onClick={() => updateFormulaField("type", "by_shipment")}
                    disabled={!isEditMode}
                    className={`px-2 sm:px-3 py-1 rounded text-xs font-medium transition-all ${
                      paymentFormula?.type === "by_shipment"
                        ? "bg-brand text-white"
                        : "bg-ui-surface-sunken text-ui-text hover:bg-ui-border"
                    } ${!isEditMode ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <span className="hidden sm:inline">{t("controls.byCount")}</span>
                    <span className="sm:hidden">{t("controls.count")}</span>
                  </button>
                  <button
                    onClick={() => updateFormulaField("type", "by_payment")}
                    disabled={!isEditMode}
                    className={`px-2 sm:px-3 py-1 rounded text-xs font-medium transition-all ${
                      paymentFormula?.type === "by_payment"
                        ? "bg-brand text-white"
                        : "bg-ui-surface-sunken text-ui-text hover:bg-ui-border"
                    } ${!isEditMode ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <span className="hidden sm:inline">{t("controls.byAmount")}</span>
                    <span className="sm:hidden">{t("controls.amount")}</span>
                  </button>
                </div>
              </div>
            </div>
            {/* Add New Tier */}
            {isEditMode && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-ui-text">
                    {t("controls.addNewTier")}
                  </label>
                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                    <CustomInput
                      value={newDetail.min}
                      onChange={(e) =>
                        setNewDetail((prev) => ({
                          ...prev,
                          min: e.target.value,
                        }))
                      }
                      placeholder={t("controls.min")}
                      inputClassName="h-7 text-xs"
                      className="flex-1"
                    />
                    <CustomInput
                      value={newDetail.max}
                      onChange={(e) =>
                        setNewDetail((prev) => ({
                          ...prev,
                          max: e.target.value,
                        }))
                      }
                      placeholder={t("controls.max")}
                      inputClassName="h-7 text-xs"
                      className="flex-1"
                    />
                    <BoltButton
                      label={t("common:actions.add")}
                      variant="primary"
                      size="sm"
                      onClick={addFormulaDetail}
                      className="h-7 px-2 text-xs w-full sm:w-16"
                    />
                  </div>
                </div>
              </div>
            )}
            {error && (
              <div className="p-2 bg-state-danger-bg border border-state-danger-border rounded-md">
                <div className="flex items-center">
                  <MdOutlineErrorOutline
                    className="text-state-danger mr-2"
                    size={12}
                  />
                  <span className="text-state-danger text-xs">{error}</span>
                </div>
              </div>
            )}

            {/* Side-by-Side Tables Layout */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
              {/* Formula Details Table */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-ui-text">
                  {t("controls.pricingTiers")}
                </h3>
                {paymentFormula?.formula_details?.length > 0 ? (
                  <div className="bg-ui-surface border border-ui-border rounded-lg overflow-hidden">
                    <table className="min-w-full">
                      <thead className="bg-ui-surface-sunken">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-ui-text-muted uppercase tracking-wider">
                            {t("controls.minOpen")}
                            {paymentFormula?.type === "by_shipment"
                              ? t("controls.count")
                              : "$"}
                            )
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-ui-text-muted uppercase tracking-wider">
                            {t("controls.maxOpen")}
                            {paymentFormula?.type === "by_shipment"
                              ? t("controls.count")
                              : "$"}
                            )
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-ui-text-muted uppercase tracking-wider">
                            {t("controls.feeOpen")}
                            {paymentFormula?.type === "by_shipment"
                              ? t("controls.count")
                              : "$"}
                            )
                          </th>
                          {isEditMode && (
                            <th className="px-3 py-2 text-left text-xs font-medium text-ui-text-muted uppercase tracking-wider">
                              {t("controls.action")}
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-ui-border">
                        {paymentFormula.formula_details.map((detail, index) => (
                          <tr key={index} className="hover:bg-ui-surface-sunken">
                            <td className="px-3 py-2 text-sm text-ui-text">
                              {detail.min}
                            </td>
                            <td className="px-3 py-2 text-sm text-ui-text">
                              {index ===
                              paymentFormula.formula_details.length - 1 ? (
                                <span className="text-lg font-medium">∞</span>
                              ) : (
                                detail.max
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <CustomInput
                                value={detail.value}
                                onChange={(e) =>
                                  updateFormulaDetail(
                                    index,
                                    "value",
                                    e.target.value
                                  )
                                }
                                disabled={!isEditMode}
                                className="w-20"
                                inputClassName="h-7 text-xs"
                              />
                            </td>
                            {isEditMode && (
                              <td className="px-3 py-2">
                                {index ===
                                  paymentFormula.formula_details.length - 1 && (
                                  <BoltButton
                                    icon={FaTrash}
                                    variant="danger"
                                    size="sm"
                                    onClick={() => removeFormulaDetail(index)}
                                    className="h-7 w-7 p-0"
                                  />
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="bg-ui-surface-sunken rounded-lg p-4 text-center">
                    <p className="text-sm text-ui-text-muted">{t("controls.noTiersConfigured")}</p>
                  </div>
                )}
              </div>

              {/* Formula Taxes Table */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-medium text-ui-text">
                    {t("controls.taxesFees")}
                  </h3>
                  {isEditMode && (
                    <div className="flex space-x-2">
                      <select
                        value={selectedState}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value) {
                            addFormulaTax(value);
                            setSelectedState("");
                          }
                        }}
                        className="h-7 px-2 text-xs border border-ui-border rounded"
                      >
                        <option value="">{t("controls.addByState")}</option>
                        {(() => {
                          const allStates = new Set();
                          Object.entries(taxConfig).forEach(([key, config]) => {
                            config.states.forEach((state) => {
                              if (state === "Quebec") {
                                allStates.add("Quebec (GST + QST)");
                              } else {
                                allStates.add(`${state} (${config.name})`);
                              }
                            });
                          });
                          return Array.from(allStates).map((stateDisplay) => (
                            <option key={stateDisplay} value={stateDisplay}>
                              {t(`controls.stateTaxes.${stateDisplay}`, {
                                defaultValue: stateDisplay,
                              })}
                            </option>
                          ));
                        })()}
                      </select>
                      <BoltButton
                        icon={FaPlus}
                        label={t("controls.custom")}
                        variant="primary"
                        size="sm"
                        onClick={() => addFormulaTax()}
                        className="h-7 px-2 text-xs"
                      />
                    </div>
                  )}
                </div>

                {paymentFormula?.formula_taxes?.length > 0 ? (
                  <div className="bg-ui-surface border border-ui-border rounded-lg overflow-hidden mt-1">
                    <table className="min-w-full">
                      <thead className="bg-ui-surface-sunken">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-ui-text-muted uppercase tracking-wider">
                            {t("controls.taxName")}
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-ui-text-muted uppercase tracking-wider">
                            {t("controls.rate")}
                          </th>
                          {isEditMode && (
                            <th className="px-3 py-2 text-left text-xs font-medium text-ui-text-muted uppercase tracking-wider">
                              {t("controls.action")}
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-ui-border">
                        {paymentFormula.formula_taxes.map((tax, index) => (
                          <tr key={index} className="hover:bg-ui-surface-sunken">
                            <td className="px-3 py-2">
                              <CustomInput
                                value={tax.name}
                                onChange={(e) =>
                                  updateFormulaTax(
                                    index,
                                    "name",
                                    e.target.value
                                  )
                                }
                                disabled={!isEditMode}
                                placeholder={t("controls.taxNamePlaceholder")}
                                className="w-full"
                                inputClassName="h-7 text-xs w-28"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <CustomInput
                                value={tax.value}
                                onChange={(e) =>
                                  updateFormulaTax(
                                    index,
                                    "value",
                                    e.target.value
                                  )
                                }
                                disabled={!isEditMode}
                                placeholder="0.00"
                                type="number"
                                className="w-28"
                                inputClassName="h-7 text-xs"
                              />
                            </td>
                            {isEditMode && (
                              <td className="px-3 py-2">
                                <BoltButton
                                  icon={FaTrash}
                                  variant="danger"
                                  size="sm"
                                  onClick={() => removeFormulaTax(index)}
                                  className="h-7 w-7 p-0"
                                />
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="bg-ui-surface-sunken rounded-lg p-4 text-center mt-0">
                    <p className="text-sm text-ui-text-muted">{t("controls.noTaxesConfigured")}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            {isEditMode && (
              <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-2 pt-3 border-t border-ui-border">
                <BoltButton
                  label={t("common:actions.cancel")}
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    setIsEditMode(false);
                    setIsCreatingNew(false);

                    if (isCreatingNew) {
                      setPaymentFormula(userDefaultFormula);
                    }
                  }}
                  className="h-8 px-3 text-xs w-full sm:w-auto"
                />
                <BoltButton
                  label={loading ? t("controls.saving") : t("common:actions.save")}
                  variant="primary"
                  size="sm"
                  onClick={saveFormula}
                  disabled={loading}
                  loading={loading}
                  className="h-8 px-3 text-xs w-full sm:w-auto"
                />
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {/* Markup Configuration */}
            <div className="bg-ui-surface-sunken rounded-lg p-3 sm:p-4">
              <h3 className="text-xs font-medium text-ui-text mb-3">
                {t("controls.serviceChargeConfiguration")}
              </h3>
              <div className="max-w-sm">
                <CustomInput
                  label={t("controls.serviceChargePercentage")}
                  value={markupFormula?.formula_details?.[0]?.value || ""}
                  onChange={(e) => updateMarkupValue(e.target.value)}
                  placeholder={t("controls.enterPercentage")}
                  type="number"
                  disabled={!isEditMode}
                  inputClassName="h-8 text-sm"
                />
                <p className="mt-2 text-xs text-ui-text-muted">
                  {t("controls.appliedToAllShipmentsFor")}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            {isEditMode && (
              <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-2 pt-3 border-t border-ui-border">
                <BoltButton
                  label={t("common:actions.cancel")}
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    setIsEditMode(false);
                    setIsCreatingNew(false);

                    if (isCreatingNew) {
                      setMarkupFormula(userDefaultMarkup);
                    }
                  }}
                  className="h-8 px-3 text-xs w-full sm:w-auto"
                />
                <BoltButton
                  label={loading ? t("controls.saving") : t("common:actions.save")}
                  variant="primary"
                  size="sm"
                  onClick={saveMarkup}
                  disabled={loading}
                  loading={loading}
                  className="h-8 px-3 text-xs w-full sm:w-auto"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <Confirm
        open={!!confirmDelete}
        setOpen={setConfirmDelete}
        handleConfirm={resetToDefault}
        text={t("controls.confirmResetDefault")}
        loader={loading}
      />
    </div>
  );
};

export default Formula;
